import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Button } from '@/shared/components/ui';
import { extractApiError } from '@/shared/lib/extractApiError';
import { formatBookingClock } from '@/shared/utils/bookingWhen';
import { bookingKeys } from '@/features/booking/api/booking.api';
import { guideBookingsKeys } from '@/features/guide/api/guide-bookings.api';
import { guideFinanceKeys } from '@/features/guide-finance/api/guide-finance.api';
import { completePaymentCheckout } from '@/features/booking/utils/verifyPaymentAfterCheckout';
import type { TripExtension } from '@/types';
import { tripExtensionApi, tripExtensionKeys } from '../api/tripExtension.api';
import { useCountdown } from '../hooks/useCountdown';
import {
  EXTENSION_STATUS_COLOR,
  EXTENSION_STATUS_LABEL,
  formatExtensionMoney,
  formatExtraHours,
} from '../utils/extensionDisplay';

interface ExtensionStatusCardProps {
  extension: TripExtension;
  viewer: 'traveler' | 'guide';
  /** Extra context shown under the title (guide dashboard: traveler and trip). */
  subtitle?: string;
}

type Action = 'accept' | 'approve' | 'decline' | 'cancel' | 'pay';

export function ExtensionStatusCard({ extension, viewer, subtitle }: ExtensionStatusCardProps) {
  const queryClient = useQueryClient();
  const countdown = useCountdown(extension.isOpen ? extension.expiresAt : undefined, extension.serverNow);
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: tripExtensionKeys.all });
    void queryClient.invalidateQueries({ queryKey: bookingKeys.all });
    void queryClient.invalidateQueries({ queryKey: guideBookingsKeys.all });
    void queryClient.invalidateQueries({ queryKey: guideFinanceKeys.all });
  };

  const mutation = useMutation({
    mutationFn: async (action: Action) => {
      setNotice(null);
      switch (action) {
        case 'accept':
          return tripExtensionApi.accept(extension.id);
        case 'decline':
          return tripExtensionApi.decline(extension.id);
        case 'cancel':
          return tripExtensionApi.cancel(extension.id);
        case 'approve':
        case 'pay': {
          if (action === 'approve') await tripExtensionApi.approve(extension.id);
          const checkout = await tripExtensionApi.checkout(
            extension.id,
            `${window.location.origin}/payment/return`
          );
          const payment = await completePaymentCheckout(checkout);
          // null = redirected to the gateway; the return page takes over from here.
          if (payment) {
            setNotice('Payment received. Confirming your extra time…');
          }
          return checkout.extension;
        }
      }
    },
    onSettled: refresh,
  });

  const { status, requestedBy, isOpen } = extension;
  const iRequested = requestedBy === viewer;
  const busy = mutation.isPending;
  const holdOver = isOpen && countdown.expired;

  const actions: Array<{ key: Action; label: string; intent: 'primary-dark' | 'secondary-outlined' }> = [];
  if (!holdOver) {
    if (status === 'REQUESTED' && !iRequested) {
      if (viewer === 'guide') actions.push({ key: 'accept', label: 'Accept', intent: 'primary-dark' });
      else actions.push({ key: 'approve', label: 'Approve & pay', intent: 'primary-dark' });
      actions.push({ key: 'decline', label: 'Decline', intent: 'secondary-outlined' });
    }
    if (status === 'REQUESTED' && iRequested) {
      actions.push({ key: 'cancel', label: 'Withdraw', intent: 'secondary-outlined' });
    }
    if (viewer === 'traveler' && (status === 'ACCEPTED' || status === 'PAYMENT_FAILED')) {
      actions.push({
        key: 'pay',
        label: status === 'PAYMENT_FAILED' ? 'Retry payment' : 'Pay now',
        intent: 'primary-dark',
      });
      actions.push({ key: 'cancel', label: 'Not now', intent: 'secondary-outlined' });
    }
    if (viewer === 'traveler' && status === 'PAYMENT_PENDING') {
      actions.push({ key: 'pay', label: 'Continue payment', intent: 'primary-dark' });
    }
  }

  const hint = (() => {
    if (holdOver) return 'Time is up — this request is being released.';
    switch (status) {
      case 'REQUESTED':
        if (iRequested) return viewer === 'guide' ? 'Waiting for the traveler to approve.' : 'Waiting for your guide to accept.';
        return viewer === 'guide' ? 'The traveler asked for more time.' : 'Your guide suggested more time.';
      case 'ACCEPTED':
        return viewer === 'guide' ? 'Waiting for the traveler to pay.' : 'Accepted — pay to confirm the extra time.';
      case 'PAYMENT_PENDING':
        return viewer === 'guide'
          ? 'The traveler is paying now.'
          : 'Checking your payment. If you closed the payment window, you can continue it.';
      case 'PAYMENT_FAILED':
        return viewer === 'guide' ? 'The traveler’s payment failed; they can retry.' : 'Your payment did not go through. You can try again.';
      case 'PAID':
        return `Confirmed. The trip now ends at ${formatBookingClock(extension.toEndAt)}.`;
      case 'DECLINED':
        return 'Declined. The trip end time stays the same.';
      case 'EXPIRED':
        return 'Not completed in time, so the time was released.';
      case 'CANCELLED':
        return 'Withdrawn. The trip end time stays the same.';
      default:
        return '';
    }
  })();

  return (
    <Box
      sx={{
        border: 1,
        borderColor: isOpen ? 'primary.light' : 'divider',
        borderRadius: 2,
        p: 2,
        bgcolor: isOpen ? 'action.hover' : 'background.paper',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            {formatExtraHours(extension.hours)} · {formatBookingClock(extension.fromEndAt)} →{' '}
            {formatBookingClock(extension.toEndAt)}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" component="p">
              {subtitle}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            {requestedBy === 'traveler' ? 'Requested by traveler' : 'Suggested by guide'} ·{' '}
            {formatExtensionMoney(extension.totalAmount, extension.currency)}
            {viewer === 'guide' && extension.estimatedGuideAmount != null
              ? ` · you earn ≈ ${formatExtensionMoney(extension.estimatedGuideAmount, extension.currency)}`
              : ''}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={EXTENSION_STATUS_LABEL[status]}
          color={EXTENSION_STATUS_COLOR[status]}
          variant={status === 'PAID' ? 'filled' : 'outlined'}
        />
      </Stack>

      <Typography variant="body2" sx={{ mt: 1 }}>
        {hint}
      </Typography>

      {isOpen && !holdOver && (
        <Typography
          variant="caption"
          color={countdown.secondsLeft < 120 ? 'error.main' : 'text.secondary'}
          sx={{ display: 'block', mt: 0.5, fontVariantNumeric: 'tabular-nums' }}
        >
          Held for {countdown.label} more
        </Typography>
      )}

      {notice && (
        <Alert severity="success" sx={{ mt: 1.5 }}>
          {notice}
        </Alert>
      )}
      {mutation.isError && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {extractApiError(mutation.error).message}
        </Alert>
      )}

      {actions.length > 0 && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }}>
          {actions.map((action) => (
            <Button
              key={action.key}
              intent={action.intent}
              size="small"
              disabled={busy}
              onClick={() => mutation.mutate(action.key)}
            >
              {busy && mutation.variables === action.key ? 'Please wait…' : action.label}
            </Button>
          ))}
        </Stack>
      )}
    </Box>
  );
}
